import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import ptbr from 'dayjs/locale/pt-br';

import { monthsInPortuguese } from './constants/common';
import { DateOption, DiffParam, MonthRange, WithDate } from './types';

dayjs.extend(customParseFormat);

export type MonthsInPortugueseRangeForYear = Record<string, { label: string; month: number }[]>;

/**
 * PT-BR configured dayjs module instance.
 * */
export const $dayjs = dayjs;

export const parseStringToDate = (str: string): Date => dayjs(str).toDate();

export const formatDate = (date: Date, template = 'DD MMM YYYY') => dayjs(date).locale(ptbr).format(template);

export const englishFormatDateFromString = (
  dateStr: string,
  inputFormat: string,
  outputFormat = 'DD/MM/YYYY, HH:mm',
): string => dayjs(dateStr, inputFormat).format(outputFormat);

export const formatDateFromString = (dateStr: string, inputFormat: string, outputFormat = 'DD/MM/YYYY, HH:mm') =>
  dayjs(dateStr, inputFormat, 'pt-br').format(outputFormat);

export const getDiffFromDateStr = (firstDate: string, secondDate: string, diffParam: DiffParam): number => {
  const date1 = dayjs(firstDate);
  const date2 = dayjs(secondDate);

  return date1.diff(date2, diffParam);
};

export const formatMonthYear = (date: string | Dayjs): string => {
  return dayjs(date)
    .locale('pt-br')
    .format('MMMM/YYYY')
    .replace(/^./, (c) => c.toUpperCase());
};

export const getMonthRange = (
  startDate: string,
  endDate: string,
  template = 'YYYY-MM-DD',
): MonthsInPortugueseRangeForYear => {
  const start = dayjs(startDate, template).startOf('month');
  const end = dayjs(endDate, template).startOf('month');

  const diff = end.diff(start, 'month') + 1;

  const monthsGroupedByYear: Record<string, { label: string; month: number }[]> = {};

  Array.from({ length: diff }, (_, i) => {
    const currentMonth = start.add(i, 'month');
    const monthNumber = currentMonth.month() + 1;
    const year = currentMonth.year();

    const monthLabel = monthsInPortuguese.find((m) => m.month === monthNumber)?.label ?? '';

    if (!monthsGroupedByYear[year]) {
      monthsGroupedByYear[year] = [];
    }

    monthsGroupedByYear[year].push({ label: monthLabel, month: monthNumber });
  });

  return monthsGroupedByYear;
};

export function buildDateOptions(monthsRange: MonthRange, order: 'asc' | 'desc' = 'desc'): DateOption[] {
  const options = Object.entries(monthsRange).flatMap(([year, months]) =>
    months.map(({ label, month }) => ({
      label: `${label}/${year}`,
      value: `${year}-${String(month).padStart(2, '0')}`,
    })),
  );

  return options.sort((a, b) => (order === 'desc' ? (a.value > b.value ? -1 : 1) : a.value > b.value ? 1 : -1));
}

export function buildMonthsRangeSparse<T extends WithDate>(reports: T[]): MonthsInPortugueseRangeForYear {
  const grouped: MonthsInPortugueseRangeForYear = {};

  reports.forEach((r) => {
    const d = $dayjs(r.date);
    const year = d.year().toString();
    const monthNum = d.month() + 1;
    const monthLabel = monthsInPortuguese.find((m) => m.month === monthNum)?.label ?? '';

    if (!grouped[year]) grouped[year] = [];

    if (!grouped[year].some((m) => m.month === monthNum)) {
      grouped[year].push({ label: monthLabel, month: monthNum });
    }
  });

  Object.values(grouped).forEach((arr) => arr.sort((a, b) => a.month - b.month));

  return grouped;
}

export function parseDateFromValue(value: string): Date {
  const day = value.slice(0, 2);
  const month = value.slice(2, 4);
  const year = value.slice(4);
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date;
}

export const formatDateToMonthYear = (dateStr: string): string => {
  const [year, month] = dateStr.split('-');

  const monthMap: Record<string, string> = {
    '01': 'Jan',
    '02': 'Fev',
    '03': 'Mar',
    '04': 'Abr',
    '05': 'Mai',
    '06': 'Jun',
    '07': 'Jul',
    '08': 'Ago',
    '09': 'Set',
    '10': 'Out',
    '11': 'Nov',
    '12': 'Dez',
  };

  const monthName = monthMap[month];
  const shortYear = year.slice(-2);

  return `${monthName}/${shortYear}`;
};

export const parseMonthYearToISO = (monthYear: string): string => {
  const [month, year] = monthYear.split('/').map(Number);
  const date = new Date(Date.UTC(year, month - 1, 1));
  return date.toISOString();
};

export const formatDateToDayMonth = (dateStr: string): string => dayjs(dateStr).format('DD/MM/YYYY');

export const buildYearOptions = (year: number, yearsAhead: number, yearsBack: number) => {
  const startYear = year - yearsBack;
  const endYear = year + yearsAhead;

  const options: DateOption[] = [];
  for (let y = startYear; y <= endYear; y++) {
    const yearStr = y.toString();
    options.push({ label: yearStr, value: yearStr });
  }

  return options;
};

export const buildMonthlyPeriodOptions = (initialDateStr: string, includeAllOption = false) => {
  const defaultFormat = 'YYYY-MM-DD';
  const labelFormat = 'MMMM YYYY';
  const startDate = dayjs(initialDateStr, defaultFormat);
  if (!startDate.isValid()) {
    return [];
  }
  const endDate = dayjs();
  const diff = endDate.diff(startDate, 'month') + 1;

  const options = Array.from({ length: diff }, (_, index) => {
    const date = startDate.add(index, 'month');
    const value = date.format(defaultFormat);
    const labelRaw = formatDateFromString(value, defaultFormat, labelFormat);
    const label = labelRaw.charAt(0).toUpperCase() + labelRaw.slice(1);
    return { label, value };
  }).reverse();

  if (includeAllOption) {
    options.unshift({ label: 'Todos os meses', value: 'all' });
  }

  return options;
};

export const buildMonthlyPeriodOptionsFromDates = (dates: string[], includeAllOption = false) => {
  const defaultFormat = 'YYYY-MM-DD';
  const uniqueDates = Array.from(
    new Set(
      dates
        .filter(Boolean)
        .map((date) => {
          const parsed = dayjs(date);
          if (!parsed.isValid()) return null;
          return parsed.startOf('month').format(defaultFormat);
        })
        .filter((value): value is string => Boolean(value)),
    ),
  );

  const sortedDates = uniqueDates.sort((a, b) => dayjs(b).valueOf() - dayjs(a).valueOf());

  const options = sortedDates.map((value) => {
    const labelRaw = formatDateFromString(value, defaultFormat, 'MMMM / YYYY');
    const label = labelRaw.charAt(0).toUpperCase() + labelRaw.slice(1);
    return { label, value };
  });

  if (includeAllOption) {
    options.unshift({ label: 'Todos os meses', value: 'all' });
  }

  return options;
};

export const buildPeriodOptionsFromInitialDate = (initialDateStr: string) => {
  const defaultFormat = 'YYYY-MM-DD';
  const labelFormat = 'MMMM YYYY';
  const startDate = dayjs(initialDateStr, defaultFormat);

  if (!startDate.isValid()) {
    return [];
  }

  const endDate = dayjs();
  const diff = endDate.diff(startDate, 'month') + 1;

  return Array.from({ length: diff }, (_, index) => {
    const date = startDate.add(index, 'month');
    const value = date.format(defaultFormat);
    const labelRaw = formatDateFromString(value, defaultFormat, labelFormat);
    const label = labelRaw.charAt(0).toUpperCase() + labelRaw.slice(1);

    return { label, value };
  }).reverse();
};

export const getPreviousMonthValue = (): string => {
  return dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
};

export const getCurrentMonthValue = (): string => {
  return dayjs().startOf('month').format('YYYY-MM-DD');
};
